import { useCallback, useEffect, useRef } from 'react'

import { TwoColumnLayout } from '@factorialco/f0-react'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { EmployeeWidgetPoliciesDocument, ProfileOverviewQuery } from 'generated/resources/graphql'
import { compact, keyBy } from 'lodash'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { i18nDateToString } from 'modules/core/lib/dates'
import { isModuleAvailabilityEnabled } from 'modules/core/lib/isModuleAvailabilityEnabled'
import { OnWidgetsEndLoadingParams, WidgetsProvider } from 'modules/core/lib/widgets'

import { buildEmployeePulseWidget } from 'modules/engagement/components/EmployeePulseWidget'
import { buildOneOnOnesWidget } from 'modules/one_on_ones/components/UpcomingMeetings'
import { DetailsSidebar } from 'modules/profile/components/DetailsSidebar'
import { buildExpensesWidget } from 'modules/profile/components/Insights/Expenses'
import { buildGoalsWidget } from 'modules/profile/components/Insights/Goals'
import { buildManagementWidget } from 'modules/profile/components/Insights/Management'
import { buildPerformanceWidget } from 'modules/profile/components/Insights/Performance'
import {
  buildProjectsUpsellWidget,
  buildProjectsWidget,
} from 'modules/profile/components/Insights/Projects'
import { buildSalaryWidget } from 'modules/profile/components/Insights/Salary'
import { buildShiftsWidget } from 'modules/profile/components/Insights/Shifts'
import { buildStatusWidget } from 'modules/profile/components/Insights/Status'
import { buildTasksWidget } from 'modules/profile/components/Insights/Tasks'
import { buildTimeOffWidget } from 'modules/profile/components/Insights/Timeoff'
import { buildTimesheetWidget } from 'modules/profile/components/Insights/Timesheet'
import { buildTrainingsWidget } from 'modules/profile/components/Insights/Trainings'
import { WidgetDashboard } from 'modules/profile/components/Insights/widgets'

interface OverviewInformationProps {
  data: ProfileOverviewQuery
}

const OverviewInformation: React.FC<OverviewInformationProps> = ({ data }) => {
  const { company, employee } = useCurrentSession()
  const employeeData = data.employees.employee
  const accessId = employeeData?.access?.id
  const today = i18nDateToString(moment(), 'backend')
  const currency = employeeData?.legalEntity?.currency ?? 'eur'
  const firstDayOfCurrentMonth = i18nDateToString(moment().startOf('month'), 'backend')
  const hasForfaitYours = !!data.contracts.referenceContractsConnection?.nodes[0]?.frForfaitJours

  const moduleAvailabilities = data?.companies.moduleAvailabilitiesConnection.nodes
  const features = keyBy(data?.features.featuresConnection.nodes, 'name')

  const timeOffEnabled = isModuleAvailabilityEnabled(moduleAvailabilities, AvailableModules.timeoff)
  const trainingsEnabled = isModuleAvailabilityEnabled(
    moduleAvailabilities,
    AvailableModules.trainings
  )
  const performanceEnabled = isModuleAvailabilityEnabled(
    moduleAvailabilities,
    AvailableModules.review_process
  )
  const shiftManagementEnabled = isModuleAvailabilityEnabled(
    moduleAvailabilities,
    AvailableModules.shift_management
  )
  const projectsEnabled = isModuleAvailabilityEnabled(
    moduleAvailabilities,
    AvailableModules.projects
  )
  const expensesEnabled = isModuleAvailabilityEnabled(
    moduleAvailabilities,
    AvailableModules.expenses
  )
  const timeTrackingEnabled = isModuleAvailabilityEnabled(
    moduleAvailabilities,
    AvailableModules.time_tracking
  )
  const goalsEnabled = isModuleAvailabilityEnabled(moduleAvailabilities, AvailableModules.goals)
  // NOTE: This manager check might be wrong in the sense that an employee might not have reports now
  // but they might have reports in the past. In which case we want them to see the widgets.
  const targetIsManager = employeeData?.reportsConnection?.totalCount ?? 0 > 0

  const employeePulseSettingsEnabled =
    data?.engagement?.settingsConnection?.edges.at(0)?.node.employeePulseEnabled

  const employeePulseFeatureEnabled =
    features['engagement_employee_pulse_questions']?.enabled ?? false

  const oneOnOnesEnabled = features['one_on_ones']?.enabled ?? false
  const employeeContextEnabled =
    targetIsManager && (features['dev_performance_employee_context']?.enabled ?? false)

  const hasFlexibleTrackingEnabled = !!data.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_flexible_tracking
  )?.available
  const timeTrackingManagementEnabled = !!data.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.time_tracking_management
  )?.available
  const employeePulseEnabled =
    employeePulseSettingsEnabled &&
    (!!data.companies.featureAvailabilitiesConnection.nodes.find(
      (feature) => feature.id === Features.engagement_employee_pulse_questions
    )?.available ||
      employeePulseFeatureEnabled)
  const widgetsInfo = useRef<OnWidgetsEndLoadingParams>()

  const handleWidgetsEndLoading = useCallback((info: OnWidgetsEndLoadingParams) => {
    widgetsInfo.current = info
  }, [])

  useEffect(
    () => () => {
      if (widgetsInfo.current) {
        track(trackingEvents.profile.leaveEmployeeProfileOverview, {
          widgets: widgetsInfo.current.shownWidgets,
          loading_time: widgetsInfo.current.loadingTime,
          empty_widgets: widgetsInfo.current.emptyWidgets,
        })
      }
    },
    []
  )

  if (!accessId || !employeeData?.id) return null

  const attendable = employeeData?.attendable

  const isCurrentEmployee = employee?.id === employeeData.id

  const widgets = compact([
    employeeContextEnabled && buildManagementWidget(),
    timeTrackingManagementEnabled && timeTrackingEnabled && attendable && buildStatusWidget(),
    shiftManagementEnabled && buildShiftsWidget(),
    timeOffEnabled && buildTimeOffWidget(),
    timeTrackingManagementEnabled &&
      timeTrackingEnabled &&
      attendable &&
      !hasForfaitYours &&
      buildTimesheetWidget(),
    performanceEnabled && buildPerformanceWidget(),
    employeePulseEnabled && buildEmployeePulseWidget(),
    goalsEnabled && buildGoalsWidget(),
    trainingsEnabled && buildTrainingsWidget(),
    performanceEnabled && oneOnOnesEnabled && isCurrentEmployee && buildOneOnOnesWidget(),
    buildTasksWidget(),
    projectsEnabled ? buildProjectsWidget() : buildProjectsUpsellWidget(),
    expensesEnabled && buildExpensesWidget(),
    buildSalaryWidget(),
  ])

  const mainContent = (
    <WidgetDashboard
      widgets={widgets}
      employeeId={employeeData.id}
      policiesQuery={EmployeeWidgetPoliciesDocument}
      extractPolicies={(policiesData) =>
        policiesData?.employees.employee?._policiesCollection ?? []
      }
      args={{
        accessId,
        employeeId: employeeData.id,
        startOn: today,
        startAt: today,
        endsOn: today,
        from: today,
        endOn: today,
        startsOn: firstDayOfCurrentMonth,
        firstDayOfCurrentMonth,
        managerId: employeeData.id,
        companyId: company.id,
        hasFlexibleTracking: hasFlexibleTrackingEnabled,
        participantIds: [employeeData.id], // for one on ones widget
        currency: currency, // for expenses widget
        featureAvailabilityIds: [Features.compensations_management], // for salary widget
      }}
    />
  )

  const sideContent = <DetailsSidebar data={data} />

  return (
    <WidgetsProvider widgets={widgets} onWidgetsEndLoading={handleWidgetsEndLoading}>
      <TwoColumnLayout sideContent={sideContent}>{mainContent}</TwoColumnLayout>
    </WidgetsProvider>
  )
}

export default OverviewInformation
